import type { JSX } from "react";
import { useLocation, useNavigate } from "react-router";
import { LogOut, ChevronsUpDown, X } from "lucide-react";
import { cn } from "@/shared/utils.js";
import { useAuth } from "../../features/auth/AuthProvider.js";
import { primaryNavItems, settingsNavItems } from "./navItems.js";
import {
  Sheet,
  SheetContent,
  SheetTitle,
} from "@/ui/sheet.js";

// The itemPath === "/" branch is unreachable today (Library now lives at
// /recipes) but is kept exact-match on purpose: Plan 12 Task 2 attaches a
// "today" nav item at "/" and it must NOT light up for every route (which
// startsWith("/") would do).
function isActive(pathname: string, itemPath: string): boolean {
  if (itemPath === "/") return pathname === "/";
  return pathname === itemPath || pathname.startsWith(itemPath + "/");
}

interface AppDrawerProps {
  open: boolean;
  onClose: () => void;
}

export function AppDrawer({ open, onClose }: AppDrawerProps): JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();
  const auth = useAuth();
  const { me } = auth;

  const currentHouseholdId = me?.user.currentHouseholdId ?? null;
  const householdName =
    me?.memberships.find((m) => m.householdId === currentHouseholdId)
      ?.householdName ?? "";
  const householdInitial = householdName[0]?.toUpperCase() ?? "?";

  const displayName = me?.user.displayName ?? "";
  const email = me?.user.email ?? "";
  const initials = displayName[0]?.toUpperCase() ?? "?";

  function handleNavigate(path: string): void {
    void navigate(path);
    onClose();
  }

  return (
    <Sheet open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
      <SheetContent
        side="right"
        showCloseButton={false}
        className="w-[80vw] max-w-sm bg-zinc-900 border-l border-zinc-800 p-0 gap-0"
      >
        {/* Radix Dialog requires a title for screen-reader announcement. */}
        <SheetTitle className="sr-only">Navigation</SheetTitle>
        {/* Sheet header: avatar + name + close */}
        <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <span className="w-12 h-12 rounded-full bg-zinc-700 text-zinc-100 text-sm font-medium flex items-center justify-center shrink-0">
              {initials}
            </span>
            <div className="min-w-0">
              <div className="text-sm font-medium text-zinc-50 truncate">{displayName}</div>
              <div className="text-[0.6875rem] text-zinc-500 truncate">{email}</div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-12 h-12 rounded-md hover:bg-zinc-800 flex items-center justify-center text-zinc-400 hover:text-zinc-200 shrink-0"
            aria-label="Close navigation menu"
          >
            <X className="h-5 w-5" aria-hidden />
          </button>
        </div>

        {/* Household chip */}
        <div className="px-3 py-3 border-b border-zinc-800">
          <button className="w-full text-left rounded-md px-2 py-2 hover:bg-zinc-800 flex items-center gap-2 text-sm">
            <span className="w-7 h-7 rounded-md bg-emerald-700 flex items-center justify-center text-xs font-medium text-emerald-50">
              {householdInitial}
            </span>
            <span className="text-zinc-50 flex-1 truncate">{householdName}</span>
            <ChevronsUpDown className="h-4 w-4 text-zinc-400 shrink-0" aria-hidden />
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-2 py-3 space-y-0.5 overflow-y-auto" aria-label="Main navigation">
          {primaryNavItems.map((item) => {
            const active = isActive(location.pathname, item.path);
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                onClick={() => { handleNavigate(item.path); }}
                className={cn(
                  "relative w-full flex items-center gap-3 rounded-md px-3 py-3 text-sm outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-emerald-500/50",
                  active
                    ? "text-emerald-400 bg-zinc-800"
                    : "text-zinc-300 hover:bg-zinc-800 hover:text-zinc-50",
                )}
                aria-current={active ? "page" : undefined}
              >
                {active && (
                  <span className="absolute left-0 top-1 bottom-1 w-0.5 rounded-r bg-emerald-500" aria-hidden />
                )}
                <Icon className="h-5 w-5" aria-hidden />
                <span>{item.label}</span>
              </button>
            );
          })}

          <div className="pt-4 pb-1 px-3 text-[0.6875rem] font-medium uppercase tracking-wider text-zinc-500">
            Settings
          </div>

          {settingsNavItems.map((item) => {
            const active = isActive(location.pathname, item.path);
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                onClick={() => { handleNavigate(item.path); }}
                className={cn(
                  "relative w-full flex items-center gap-3 rounded-md px-3 py-3 text-sm outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-emerald-500/50",
                  active
                    ? "text-emerald-400 bg-zinc-800"
                    : "text-zinc-300 hover:bg-zinc-800 hover:text-zinc-50",
                )}
                aria-current={active ? "page" : undefined}
              >
                {active && (
                  <span className="absolute left-0 top-1 bottom-1 w-0.5 rounded-r bg-emerald-500" aria-hidden />
                )}
                <Icon className="h-5 w-5" aria-hidden />
                <span>{item.label}</span>
              </button>
            );
          })}
        </nav>

        {/* Sign out */}
        <div className="border-t border-zinc-800 p-2">
          <button
            onClick={() => { void auth.signOut(); onClose(); }}
            className="w-full text-left rounded-md px-3 py-3 text-sm outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-emerald-500/50 text-zinc-300 hover:bg-zinc-800 hover:text-zinc-50 flex items-center gap-3 min-h-[3rem]"
          >
            <LogOut className="h-5 w-5" aria-hidden />
            <span>Sign out</span>
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
